import { isRecord } from "@openclaw/normalization-core/record-coerce";

export const NATIVE_HISTORY_STATE_EVENT = "openclaw:native-history-state";

export type NativeHistoryState = {
  canGoBack: boolean;
  canGoForward: boolean;
};

type NativeWebChromeWindow = Window & {
  __OPENCLAW_NATIVE_EMBED__?: unknown;
  __OPENCLAW_NATIVE_WEB_CHROME__?: boolean;
  __OPENCLAW_NATIVE_HISTORY__?: NativeHistoryState;
};

// Hosts listen from document start so they can enable the shared chrome before
// application state reads their flag or the first shell renders.
if (typeof window !== "undefined") {
  window.dispatchEvent(new Event("openclaw:native-window-chrome-available"));
}

export function isNativeWebChromeHost(): boolean {
  return (window as NativeWebChromeWindow)["__OPENCLAW_NATIVE_WEB_CHROME__"] === true;
}

export function nativeEmbedHost(): {
  platform: "ios" | "macos" | "android";
  formFactor: "phone" | "pad" | "desktop";
  surface?: "conversation";
} | null {
  // SAFETY: the host adds this optional document-start value; its shape is validated below.
  const host = (window as NativeWebChromeWindow)["__OPENCLAW_NATIVE_EMBED__"];
  if (!isRecord(host)) {
    return null;
  }
  const { platform, formFactor, surface } = host;
  return (platform === "ios" || platform === "macos" || platform === "android") &&
    (formFactor === "phone" || formFactor === "pad" || formFactor === "desktop") &&
    (surface === undefined || surface === "conversation")
    ? { platform, formFactor, ...(surface ? { surface } : {}) }
    : null;
}

// Startup consumes native authentication metadata. Retain only its host provenance,
// never credentials, for WebViews that do not opt into embedded chrome.
let nativeControlHost = false;

export function setNativeControlHost(value: boolean): void {
  nativeControlHost = value;
}

export function isNativeMobileHost(): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  const embed = nativeEmbedHost();
  if (embed) {
    return embed.platform === "ios" || embed.platform === "android";
  }
  // iPad can request a desktop user agent. A mobile browser alone is not a
  // native host, and a narrow desktop window must keep desktop behavior.
  return (
    nativeControlHost &&
    (/Android|iPhone|iPad|iPod/u.test(navigator.userAgent) ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1))
  );
}

export function isNativeEmbedHost(): boolean {
  return nativeEmbedHost() !== null;
}

export function readNativeHistoryState(): NativeHistoryState {
  const state = (window as NativeWebChromeWindow)["__OPENCLAW_NATIVE_HISTORY__"];
  return state && typeof state.canGoBack === "boolean" && typeof state.canGoForward === "boolean"
    ? state
    : { canGoBack: false, canGoForward: false };
}
